import { subscribeCicdEvents } from "../../../hooks/useCicdEvents";
// Starts snapshots and dispatches; server events refresh the observed run state.
// Nutzt bestehende Endpoints (Transport-Logik ist eingefroren):
//   POST /api/cicd/transport/snapshot   (inkl. partnerIds -> PD-Dateien im Snapshot)
//   POST /api/cicd/transport/dispatch  -> {laeufe:[{packageId,run:{id,status,html_url}|null,error?,hinweis?}]}
//                                          ODER {status:"pending_approval"}
//   GET /api/cicd/pipeline/overview?spaceId=... after server events and reconnects.
// ⛔ KEIN direkter PD-Write mehr: das Partner Directory geht ausschliesslich ueber die Pipeline
//    (partnerIds -> Snapshot -> Workflow sync-partner-directory.yml am Ziel). Der fruehere
//    Direktaufruf /api/transport/replicate-partner-directory ist abgeschaltet.

import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../utils/helpers";
import { t as tr } from "../i18n";
import { plantNamingError } from "../../../utils/transportBatchErrors";
import { requestPlantNaming } from "../utils/plantNaming";
import { transportTopicError } from "../../../utils/transportTopicErrors";
import { packageFallbackText, transportPackageError } from "../../../utils/transportPackageErrors";
import { newDispatchRequestId, recoverDispatch, dispatchDefinitelyNotStarted } from "../utils/dispatchRecovery";
import { rememberSnapshotJob, submitSnapshot, waitForSnapshotJob } from "../../../stage-branches/snapshotJobClient";
import { stageBranchesErrorDetail, stageBranchesErrorText } from "../../../stage-branches/stageBranchesActive";
// Fachlicher Text eines Stufenbranch-Codes; bei den Mehrzielcodes mit den betroffenen Stufen oder Zielen (Integration I1).
const stageBranchesErrorMessage = (error, language) => [stageBranchesErrorText(error, language), stageBranchesErrorDetail(error, { language })]
  .filter(Boolean).join(" ");

// Fetch aborts are DOMExceptions with a read-only message. Keep API metadata,
// but annotate an owned Error so timeout handling cannot throw another error.
function transportFailure(cause) {
  const error = Object.assign(new Error(cause?.message || String(cause || "")), cause);
  error.name = cause?.name || "Error";
  if (cause?.code !== undefined) error.code = cause.code;
  return error;
}

/** Hinweise zur Topic-Zuordnung aus der Snapshotantwort (Topic-Sperre nur Hinweis, Korrekturrunde 2). */
export function topicMeldungen(snapshotRes, tr) {
  return (Array.isArray(snapshotRes?.warnings) ? snapshotRes.warnings : []).filter((w) => w?.code === "STAGE_TOPIC_LOCKED")
    .flatMap((w) => (Array.isArray(w.packages) && w.packages.length ? w.packages : [w]).map((p) => ({
      art: "hinweis",
      text: w.reason === "snapshot-queued" ? tr("run.topicQueued", { pkg: p.packageId || "?" })
        : tr("run.topicMoved", { pkg: p.packageId || "?", alt: p.topicName || p.topicId || "?", neu: w.newTopicName || w.newTopicId || "?" }),
    })));
}

/** Hinweis PACKAGE_TRANSPORT_ALL_ARTIFACTS aus der Snapshotantwort (warnings oder hints, als Objekt oder Code). */
// Rückfälle auf den Weg je iFlow: Der Snapshot läuft weiter, der Grund wird als Hinweis genannt.
const RUECKFALL = new Set(["PACKAGE_EXPORT_TOO_LARGE", "PACKAGE_EXPORT_UNAVAILABLE", "PACKAGE_TRANSPORT_EXPORT_UNAVAILABLE", "PACKAGE_EXPORT_UNCLEAR", "PACKAGE_TRANSPORT_MODE_CHANGED"]);

export function packageTransportMeldungen(snapshotRes, tr, fallbackPkg, { wholeSent = false } = {}) {
  // Nur Einträge mit Code; die Klartexte in hinweise wiederholen die Nachrichten der notices.
  const list = ["warnings", "hints", "notices", "hinweise"].flatMap((key) => (Array.isArray(snapshotRes?.[key]) ? snapshotRes[key] : []));
  const codeOf = (w) => (typeof w === "string" ? (/^(PACKAGE|REFERENCE)_[A-Z_]+$/.test(w) ? w : null) : w?.code);
  // DEV183: auch Referenzhinweise (REFERENCE_*), im selben Format wie die Paketcodes.
  const unique = [...new Map(list.filter((w) => /^(PACKAGE|REFERENCE)_/.test(String(codeOf(w) || "")))
    // REFERENCE_*: je Referenz ein Eintrag, zusammengefasst über code und referenceId (nicht packageId).
    .map((w) => [String(codeOf(w)).startsWith("REFERENCE_") ? `${codeOf(w)}:${w?.referenceId || ""}` : `${codeOf(w)}:${w?.packageId || ""}`, w])).values()];
  return unique.flatMap((w) => {
    const code = codeOf(w);
    // Welle 6: Löschen und Neuimport sind beim ganzen Paket der Standardweg; die Servernotice bleibt für Bericht und Lauf.
    if (code === "PACKAGE_TRANSPORT_TARGET_REPLACED") return [];
    // Ganzes Paket nur melden, wenn der Wizard je Artefakt gesendet hat (unerwarteter Wechsel).
    if (code === "PACKAGE_TRANSPORT_ALL_ARTIFACTS") return wholeSent ? []
      : [{ art: "hinweis", text: tr("pkgT.allArtifacts", { pkg: w?.packageId || fallbackPkg || "?" }) }];
    // Rückfall: der Katalog sagt, dass das Paket je iFlow weiterläuft; die Servernachricht allein nennt nur den Grund.
    if (RUECKFALL.has(code)) return [{ art: "hinweis", text: packageFallbackText(code) || w?.message || code }];
    // Sonstige Paketcodes (Rückfall je iFlow, Umstellung eines fremden Exportordners, Löschen im Ziel): Servertext vor Katalog.
    return [{ art: "hinweis", text: (typeof w?.message === "string" && w.message.trim()) || transportPackageError(typeof w === "object" ? w : code) || code }];
  });
}

export function useCicdRun(scope) {
  // idle|snapshotting|dispatching|running|pending|staged|dispatched|done|failed
  // "staged" = mit Stufenbranches in die Pipeline gelegt; die Weitergabe beantragt das Stufen-Board.
  // "dispatched" = abgeschickt, aber kein Lauf auffindbar (siehe unten).
  const [state, setState] = useState("idle");
  const [runId, setRunId] = useState(null);
  const [runUrl, setRunUrl] = useState(null);
  const [runInfo, setRunInfo] = useState(null);
  const [error, setError] = useState(null);
  const [failure, setFailure] = useState(null);
  // Nebenmeldungen, die den Transport nicht abbrechen: nicht ladbare Artefakte,
  // uebersprungene Partner-Kennungen, Pakete ohne eigenen Lauf. Sie muessen sichtbar
  // bleiben - sonst haelt der Anwender einen Teiltransport fuer vollstaendig.
  const [meldungen, setMeldungen] = useState([]);
  const timerRef = useRef(null);
  const pollingVersion = useRef(0);
  const operationRef = useRef(null);
  const [edgeFollowups, setEdgeFollowups] = useState(null);
  const [cloudComplete, setCloudComplete] = useState(false);
  const [snapshotJob, setSnapshotJob] = useState(null);
  const snapshotSubmission = useRef(null);

  useEffect(() => {
    setState("idle"); setRunId(null); setRunUrl(null); setRunInfo(null); setError(null); setFailure(null);
    setCloudComplete(false); setEdgeFollowups(null); setMeldungen([]); setSnapshotJob(null); snapshotSubmission.current = null;
    return () => { operationRef.current?.abort(); pollingVersion.current += 1; if (timerRef.current) timerRef.current(); };
  }, [scope]);

  // Beobachtet ALLE gestarteten Laeufe in EINEM Tick. Bei mehreren Werken laeuft je
  // Zielpaket ein eigener Workflow; frueher wurde nur der erste gepollt und der
  // Assistent meldete Erfolg, waehrend Lauf 2 rot lief.
  const startPolling = useCallback((spaceId, beobachtet, edgePlan) => {
    const version = ++pollingVersion.current;
    let busy = false, observing = true;
    if (timerRef.current) timerRef.current();
    const liste = (Array.isArray(beobachtet) ? beobachtet : []).filter((l) => l && l.id != null);
    if (!liste.length) return;
    const stopObserving = () => { observing = false; if (timerRef.current) timerRef.current(); };
    const tick = async () => {
      if (!observing || busy || version !== pollingVersion.current) return;
      busy = true;
      try {
        const r = await api(`/api/cicd/pipeline/overview?spaceId=${encodeURIComponent(spaceId)}`);
        if (version !== pollingVersion.current) return;
        const runs = Array.isArray(r?.runs) ? r.runs : [];
        const stand = liste.map((l) => ({ ...l, run: runs.find((x) => String(x.id) === String(l.id)) || null }));
        // Die Live-Karte zeigt weiterhin EINEN Lauf - den zuerst gestarteten. Der
        // Gesamtzustand darunter fasst dagegen alle beobachteten Laeufe zusammen.
        const ersterTreffer = stand.find((x) => x.run);
        if (ersterTreffer) setRunInfo(ersterTreffer.run);
        const gescheitert = stand.filter((x) => x.run && x.run.status === "completed" && x.run.conclusion !== "success");
        if (gescheitert.length) {
          setRunId(gescheitert[0].id); setRunInfo(gescheitert[0].run);
          stopObserving();
          const namen = gescheitert.map((x) => x.packageId || x.id).join(", ");
          const text = gescheitert.length === 1
            ? tr("run.pkgFailed", { pkg: namen })
            : tr("run.pkgsFailed", { n: gescheitert.length, liste: namen });
          setError((prev) => (prev ? `${prev} ${text}` : text));
          setState("failed");
          return;
        }
        const alleFertig = stand.every((x) => x.run && x.run.status === "completed" && x.run.conclusion === "success");
        if (alleFertig) {
          if (edgePlan?.dispatchConfirmed === false) {
            stopObserving();
            setState("dispatched");
            return;
          }
          setCloudComplete(true);
          if (edgePlan?.pids.length) {
            setState("syncing_edge");
            let response;
            try {
              response = await api(`/api/cicd/transport/edge-followups?spaceId=${encodeURIComponent(spaceId)}&sourceSha=${encodeURIComponent(edgePlan.sourceSha)}&language=${window.__iioLang === "en" ? "en" : "de"}`);
              if (!Array.isArray(response?.jobs)) throw new Error("EDGE_REQUEST_FAILED");
            } catch {
              if (version === pollingVersion.current) setEdgeFollowups(current => ({ ...current, status: "unknown", readError: true }));
              return;
            }
            if (version !== pollingVersion.current) return;
            const jobs = response.jobs.filter(job => job.targetStage === edgePlan.targetStage);
            const rows = jobs.flatMap(job => Array.isArray(job.pids) ? job.pids : []);
            const failed = jobs.some(job => job.status === "failed") || rows.some(row => row.status === "failed");
            const retired = jobs.some(job => job.status === "retired");
            const unknown = jobs.some(job => job.status === "unknown") || rows.some(row => row.status === "unknown");
            const completed = jobs.length > 0 && jobs.every(job => job.status === "completed")
              && edgePlan.pids.every(expected => rows.some(row => row.pid === expected.pid && row.runtimeId === expected.runtimeId && row.status === "completed"));
            const status = failed ? "failed" : retired ? "retired" : unknown ? "unknown" : completed ? "completed"
              : jobs.some(job => job.status === "syncing_edge") ? "syncing_edge"
                : jobs.some(job => job.status === "verifying_cloud") ? "verifying_cloud" : "waiting_cloud";
            setEdgeFollowups({ jobs, status, readError: false });
            if (failed || retired) {
              stopObserving();
              setState(retired && !failed ? "retired" : "failed");
              return;
            }
            // The backend can reconcile unknown outcomes without dispatching again.
            if (unknown || !completed) return;
          }
          stopObserving();
          setState("done");
        }
      } catch {
        // A failed status read never proves completion; the next tick retries the read.
      } finally { busy = false; }
    };
    const initialRead = tick();
    // Preserve an event received while the initial status read is still pending.
    timerRef.current = subscribeCicdEvents(spaceId, async () => {
      await initialRead;
      if (observing && version === pollingVersion.current) await tick();
    });
  }, []);

  const start = useCallback(async ({ spaceId, userId, canonicalSpaceId = spaceId, topicId, topicSelection, sourceInstanceId, packageId, packageName, artifactIds, globalRefs, werke, configsByWerk, partnerIds, snapshotPartnerIds, pdStagesByPid, pdEdgeByPid, commitMessage, targetStage, deployment, plantChangeConfirmations, plantNaming, plantCatalogRevision, plantTargetRevisions, expectedTransportModus, sapWholePackageConfirmed, stageBranches = false, stageTargets, stageTargetsByWerk, transport }, { onSnapshotConfirmed } = {}) => {
    // Etappe C: Der Snapshot fuehrt ALLE ausgewaehlten Pids samt Stufen-Filter
    // (.stages.json je Pid), der Dispatch nur die Pids fuer die aktuelle Zielstufe.
    // Wird snapshotPartnerIds nicht mitgegeben, faellt der Snapshot auf partnerIds
    // zurueck - so bleibt jeder Aufrufer der Alt-Signatur weiter kompatibel.
    const pidsFuerSnapshot = Array.isArray(snapshotPartnerIds) ? snapshotPartnerIds : (Array.isArray(partnerIds) ? partnerIds : []);
    const pidsFuerDispatch = Array.isArray(partnerIds) ? partnerIds : [];
    operationRef.current?.abort();
    const operation = new AbortController();
    operationRef.current = operation;
    const current = () => !operation.signal.aborted && operationRef.current === operation;
    pollingVersion.current += 1;
    if (timerRef.current) timerRef.current();
    setEdgeFollowups(null);
    setCloudComplete(false);
    setState("snapshotting");
    setSnapshotJob(null);
    setError(null); setFailure(null);
    setMeldungen([]);
    setRunId(null);
    setRunUrl(null);
    setRunInfo(null);
    let snapshotRes;
    try {
      const snapshotBody = {
          spaceId,
          ...(topicSelection === "default" ? { topicSelection, topicId: null } : topicId ? { topicId, ...(topicSelection ? { topicSelection } : {}) } : {}),
          sourceInstanceId,
          expectedTransportModus,
          sapWholePackageConfirmed,
          packageId,
          packageName,
          // Pakettransport (W6.5): nur dann gesetzt, reine iFlow-Pakete senden unverändert.
          ...(transport === "package" ? { transport } : {}),
          artifactIds,
          deployment,
          targetStage,
          // Selected global references (script collections). The snapshot stores them under
          // ScriptCollections/<Id>/ and the target workflow deploys them BEFORE the iFlows.
          globalRefs: Array.isArray(globalRefs) ? globalRefs : [],
          // Selected plants: the snapshot materializes one package folder per kennung, with
          // suffixed artifact ids. Empty means no split. Jedes Werk traegt seine EIGENE
          // Artefakt-Auswahl (artifactIds) - ohne sie vervielfacht das Backend die flache
          // Liste auf alle Werke und eine Abwahl in Schritt 3 bliebe wirkungslos.
          werke: Array.isArray(werke) ? werke : [],
          plantChangeConfirmations: plantChangeConfirmations || [],
          plantNaming: requestPlantNaming(plantNaming),
          plantCatalogRevision,
          plantTargetRevisions,
          // Parameter values per plant and stage. The backend writes SAP's
          // Configuration/ConfigParams_INTEGRATION_FLOW.json per target package from them and
          // merges with the Git stock, so values of other stages survive.
          configsByWerk: configsByWerk && typeof configsByWerk === "object" ? configsByWerk : {},
          // Ausgewaehlte Partner-Kennungen mit in den Snapshot: das Backend
          // exportiert sie unter IntegrationPackages/<pkg>/PartnerDirectory/<Pid>/...
          // Ohne partnerIds bleibt das Snapshot unveraendert - PD-Dateien werden
          // nur erzeugt, wenn hier eine nicht-leere Liste ankommt.
          partnerIds: pidsFuerSnapshot,
          // Etappe C: je Pid der Stufen-Satz. Fehlt der Eintrag fuer eine Pid,
          // schreibt das Backend "*" in .stages.json (Altverhalten, alle Stufen).
          pdStagesByPid: pdStagesByPid && typeof pdStagesByPid === "object" ? pdStagesByPid : {},
          pdEdgeByPid: pdEdgeByPid || {},
          commitMessage: commitMessage || `Transport ${packageName || packageId}`,
          // Mehrzielmodus (Vertragsnachtrag V.13): Wizard-Auswahl der Ziele je Stufe als Vorbelegung des Antrags, nur Webapp.
          ...(stageTargets && typeof stageTargets === "object" ? { stageTargets } : {}),
          // Werkskatalog (W4.5): je Werk eigene Ziele, nur bei mindestens zwei Werken mit unterschiedlichen Werten.
          ...(stageTargetsByWerk && typeof stageTargetsByWerk === "object" ? { stageTargetsByWerk } : {}),
      };
      if (stageBranches) {
        const key = JSON.stringify(snapshotBody);
        if (snapshotSubmission.current?.key !== key) snapshotSubmission.current = { key, id: newDispatchRequestId() };
        snapshotBody.clientRequestId = snapshotSubmission.current.id;
      }
      snapshotRes = await submitSnapshot(snapshotBody, { signal: operation.signal });
      if (stageBranches && snapshotRes?.job?.id) {
        const job = snapshotRes.job;
        rememberSnapshotJob(userId, spaceId, job.id);
        snapshotRes = await waitForSnapshotJob(job, { spaceId, signal: operation.signal,
          onJob: next => { if (current()) setSnapshotJob(next); } });
        snapshotSubmission.current = null;
      }
    } catch (cause) {
      const e = transportFailure(cause);
      if (e.snapshotJob && ["failed", "cancelled"].includes(e.snapshotJob.state)) snapshotSubmission.current = null;
      if (!current()) throw Object.assign(e, { transportPhase: "git", transportOutcomeUnknown: true });
      setFailure(e);
      // The snapshot endpoint includes `collected` in every error after entering
      // the commit phase. Only an explicit validation rejection without that
      // marker proves that this attempt did not create a snapshot.
      e.transportPhase = "git";
      e.snapshotRejected = ([400, 403, 409, 422].includes(e.status)
        || ["SNAPSHOT_PD_SCOPE_UNREADABLE", "SNAPSHOT_QUEUE_FULL", "SNAPSHOT_QUEUE_BUSY", "PAKET_EINSTIEG_NICHT_PRUEFBAR"].includes(e.code)) && typeof e.body?.error === "string"
        && !Object.prototype.hasOwnProperty.call(e.body, "collected")
        && !e.body.commit && !e.body.sourceSha;
      if (transportTopicError(e)) e.message = transportTopicError(e);
      if (transportPackageError(e)) e.message = transportPackageError(e);
      if (typeof e.code === "string" && e.code.startsWith("PLANT_")) e.message = plantNamingError(e);
      if (stageBranchesErrorText(e)) e.message = stageBranchesErrorMessage(e, window.__iioLang);
      if (e.code === "STAGE_INPUT_INVALID" && e.details?.reason === "stage-snapshot-requires-dev-instance") e.message = tr("run.stageDevOnly");
      e.transportOutcomeUnknown = !e.snapshotRejected;
      if (e.transportOutcomeUnknown) e.message = tr("run.snapshotUnknown");
      setError(e?.message || "Snapshot fehlgeschlagen");
      setState(e.transportOutcomeUnknown ? "unknown" : "failed");
      throw e;
    }
    if (!current()) throw Object.assign(new Error(tr("run.snapshotUnknown")), { transportPhase: "git", transportOutcomeUnknown: true });

    // Nebenbefunde aus dem Snapshot sichtbar machen. Das Backend bricht bei nicht ladbaren
    // AUSGEWAEHLTEN Artefakten hart ab (502); kommen die Listen trotzdem in einer OK-Antwort
    // mit, betreffen sie Randfaelle - lautlos verschwinden duerfen sie deshalb nicht.
    const nichtGeladen = (Array.isArray(snapshotRes?.failed) ? snapshotRes.failed : []).filter(Boolean);
    const pdNichtGeladen = (Array.isArray(snapshotRes?.pdFailed) ? snapshotRes.pdFailed : []).filter(Boolean);
    const nebenmeldungen = [
      ...nichtGeladen.map((f) => ({
        art: "fehler",
        text: tr("run.artFailedItem", {
          id: f.id || f.artifactId || "?",
          grund: f.reason || f.grund || tr("run.grundUnbekannt"),
        }),
      })),
      ...pdNichtGeladen.map((p) => ({
        art: "hinweis",
        text: tr("run.pdFailedItem", {
          pid: p.pid || p.Pid || "?",
          grund: p.grund || p.reason || tr("run.grundUnbekannt"),
        }),
      })),
      // Topic-Sperre nur Hinweis (Korrekturrunde 2): Paket aus einem anderen Topic wurde umgehängt.
      ...topicMeldungen(snapshotRes, tr),
      // Pakettransport: Der Server hat das ganze Paket übernommen, obwohl der Wizard je Artefakt gesendet hat.
      ...packageTransportMeldungen(snapshotRes, tr, packageName || packageId, { wholeSent: transport === "package" }),
    ];
    if (nebenmeldungen.length) setMeldungen(nebenmeldungen);
    if (nichtGeladen.length) setError(tr("run.artFailed", { n: nichtGeladen.length }));
    if (pdNichtGeladen.length && !nichtGeladen.length) setError(tr("run.pdFailed", { n: pdNichtGeladen.length }));
    if (pdNichtGeladen.some(item => pidsFuerSnapshot.includes(item.pid || item.Pid))) {
      const message = tr("run.pdFailed", { n: pdNichtGeladen.length });
      setState("unknown");
      throw Object.assign(new Error(message), { transportPhase: "git", transportOutcomeUnknown: true });
    }

    // The target packages come from the snapshot response, they are NOT rebuilt here. The
    // snapshot just created them and knows their ids; a second naming rule maintained in parallel
    // in the frontend has been a repeat source of bugs.
    const zielPakete = (Array.isArray(snapshotRes?.pakete) ? snapshotRes.pakete : [])
      .map((p) => ({ packageId: p.packageId, packageName: p.packageName, artifactIds: p.artefakte || [] }))
      .filter((p) => p.packageId);
    if (!zielPakete.length) {
      const msg = tr("run.snapshotInvalid");
      setError(msg);
      setState("unknown");
      throw Object.assign(new Error(msg), { transportPhase: "git", transportOutcomeUnknown: true });
    }

    const confirmedSha = snapshotRes?.commit?.sha || snapshotRes?.commit?.commit?.sha;
    if (!/^[a-f0-9]{40}$/i.test(confirmedSha || "")) {
      const message = tr("run.snapshotInvalid");
      setError(message); setState("unknown");
      throw Object.assign(new Error(message), { transportPhase: "git", transportOutcomeUnknown: true });
    }
    onSnapshotConfirmed?.({ sourceSha: confirmedSha, pakete: zielPakete });
    // Mit Stufenbranches endet der Ablauf hier: kein Dispatch, die Weitergabe beantragt das Stufen-Board (Vertrag 24.1).
    if (stageBranches === true) {
      setState("staged");
      return { staged: true, pendingApproval: false, sourceSha: confirmedSha, pakete: zielPakete, failed: nichtGeladen, pdFailed: pdNichtGeladen };
    }
    setState("dispatching");
    let dispatchRes;
    const rejectConfirmedStart = result => {
      if (!dispatchDefinitelyNotStarted(result, expectedOperation)) return;
      const code = result.code || result.laeufe.find(item => item.code)?.code || "DISPATCH_NOT_STARTED";
      const message = code === "EDGE_FOLLOWUP_BUSY" ? tr("run.dispatchNotStartedEdgeBusy") : tr("run.dispatchNotStarted");
      const error = Object.assign(new Error(message), { code, body: result, status: 409,
        transportPhase: "wf", transportOutcomeUnknown: false, dispatchNotStarted: true });
      setFailure(error); setError(message); setState("failed");
      throw error;
    };
    let recoveryError;
    const expectedOperation = { dispatchRequestId: newDispatchRequestId(), spaceId, canonicalSpaceId, sourceSha: confirmedSha,
      targetStage: String(targetStage || "").toLowerCase(), packageIds: zielPakete.map(item => item.packageId) };
    const recover = async () => {
      if (!current()) return null;
      setState("dispatching"); setError(tr("run.dispatchChecking"));
      let recovered;
      try { recovered = await recoverDispatch(expectedOperation, operation.signal); }
      catch (error) { recoveryError = error; }
      if (!current()) return null;
      if (recovered) { setError(null); setFailure(null); }
      return recovered;
    };
    try {
      dispatchRes = await api("/api/cicd/transport/dispatch", {
        method: "POST",
        // One run per target package, started one after the other. With several plants this takes
        // correspondingly longer than the single call it used to be.
        timeout: 120000,
        body: JSON.stringify({
          dispatchRequestId: expectedOperation.dispatchRequestId,
          spaceId,
          // Kein stiller "qas"-Fallback mehr: das Backend prueft die Zielstufe und meldet
          // fachlich, wenn sie fehlt oder nicht vorwaerts liegt. Raten ist hier gefaehrlicher
          // als ein klarer Fehler.
          targetStage: String(targetStage || "").toLowerCase(),
          sourceSha: snapshotRes?.commit?.sha || snapshotRes?.commit?.commit?.sha,
          expectedTransportModus: deployment === undefined ? undefined : "erweitert",
          language: localStorage.getItem("iio-lang") === "en" ? "en" : "de",
          pakete: zielPakete,
          // Selektierte Partner-Directory-Pids: loesen im Ziel-Workflow den PD-Merge-Upsert aus.
          // Etappe C: hier NUR die fuer die aktuelle Zielstufe freigegebenen Pids - Pids, die
          // erst spaeter (z. B. PROD) greifen sollen, liegen im Snapshot, gehen aber nicht in
          // diesen Dispatch.
          partnerIds: pidsFuerDispatch.join(","),
          pdEdgeByPid: pdEdgeByPid || {},
          commitMessage: commitMessage || `Transport ${packageName || packageId}`,
        }),
      });
    } catch (cause) {
      const e = transportFailure(cause);
      e.transportPhase = "wf";
      if (transportTopicError(e)) e.message = transportTopicError(e);
      if (transportPackageError(e)) e.message = transportPackageError(e);
      if (String(e.code || "").startsWith("DISPATCH_OPERATION_")) e.message = tr(`run.${e.code}`);
      if (stageBranchesErrorText(e)) e.message = stageBranchesErrorMessage(e, window.__iioLang);
      e.transportOutcomeUnknown = !e.status || e.status >= 500 || e.code === "DISPATCH_OUTCOME_UNKNOWN"
        || ["DISPATCH_OPERATION_PENDING", "DISPATCH_OPERATION_CONFLICT", "DISPATCH_OPERATION_ACCESS_REVOKED"].includes(e.code);
      if (e.transportOutcomeUnknown) dispatchRes = await recover();
      if (!current()) throw Object.assign(e, { transportOutcomeUnknown: true });
      if (!dispatchRes) {
        setFailure(e);
        if (e.transportOutcomeUnknown) e.message = recoveryError?.code === "DISPATCH_OPERATION_ACCESS_REVOKED"
          ? tr("run.DISPATCH_OPERATION_ACCESS_REVOKED") : tr("run.dispatchUnknown");
        setError(e?.message || "Workflow-Start fehlgeschlagen");
        setState(e.transportOutcomeUnknown ? "unknown" : "failed");
        throw e;
      }
    }

    if (!current()) throw Object.assign(new Error(tr("run.dispatchUnknown")), { transportPhase: "wf", transportOutcomeUnknown: true });
    rejectConfirmedStart(dispatchRes);
    if (dispatchRes?.status !== "pending_approval" && !zielPakete.every(expected =>
      dispatchRes?.laeufe?.some(item => item?.packageId === expected.packageId && item.run?.id)
      || (zielPakete.length === 1 && !dispatchRes?.laeufe?.length && dispatchRes?.run?.id))) {
      const recovered = await recover();
      if (!current()) throw Object.assign(new Error(tr("run.dispatchUnknown")), { transportPhase: "wf", transportOutcomeUnknown: true });
      if (recovered) { rejectConfirmedStart(recovered); dispatchRes = recovered; }
      else setError(recoveryError?.code === "DISPATCH_OPERATION_ACCESS_REVOKED"
        ? tr("run.DISPATCH_OPERATION_ACCESS_REVOKED") : tr("run.dispatchUnknown"));
    }

    if (dispatchRes?.status === "pending_approval") {
      setState("pending");
      return { pendingApproval: true, failed: nichtGeladen, pdFailed: pdNichtGeladen };
    }

    const laeufe = Array.isArray(dispatchRes?.laeufe) ? dispatchRes.laeufe : [];
    // Do not swallow partial failures: what runs, runs - but the user has to see which package
    // did not start, otherwise they take the transport for complete.
    if (dispatchRes?.teilfehler) setError((prev) => (prev ? `${prev} ${dispatchRes.teilfehler}` : dispatchRes.teilfehler));

    // Pakete ohne eigenen Lauf: Grund bzw. Hinweis steht am Eintrag und muss sofort sichtbar
    // sein - im Polling taucht so ein Paket nie wieder auf.
    const ohneLauf = laeufe.filter((l) => !l?.run?.id);
    if (ohneLauf.length) {
      setMeldungen((prev) => [
        ...prev,
        ...ohneLauf.map((l) => ({
          art: l?.error ? "fehler" : "hinweis",
          text: tr("run.pkgLine", { pkg: l?.packageId || "?", text: l?.error || l?.hinweis || tr("run.keinLauf") }),
        })),
      ]);
    }

    // Das Backend sendet html_url (nicht url) - url bleibt nur als Fallback fuer aeltere Antworten.
    const beobachtet = laeufe
      .filter((l) => l?.run?.id)
      .map((l) => ({ id: l.run.id, packageId: l.packageId || null, url: l.run.html_url || l.run.url || null }));
    if (!beobachtet.length && dispatchRes?.run?.id) {
      beobachtet.push({
        id: dispatchRes.run.id,
        packageId: null,
        url: dispatchRes.run.html_url || dispatchRes.run.url || null,
      });
    }

    const dispatchConfirmed = !dispatchRes?.teilfehler && ohneLauf.length === 0 && zielPakete.every(expected =>
      laeufe.some(item => item?.packageId === expected.packageId && item.run?.id)
      || (zielPakete.length === 1 && !laeufe.length && dispatchRes?.run?.id));
    if (beobachtet.length && !dispatchConfirmed) setError(previous => previous || tr("log.dispatchUnconfirmed"));
    if (beobachtet.length) {
      setRunId(beobachtet[0].id);
      setRunUrl(beobachtet[0].url);
      setState("running");
      const edgeStage = String(targetStage || "").toLowerCase().replaceAll("-", "");
      const edgePids = pidsFuerDispatch.flatMap(pid => pdEdgeByPid?.[pid]?.[edgeStage]
        ? [{ pid, runtimeId: pdEdgeByPid[pid][edgeStage] }] : []);
      const sourceSha = snapshotRes?.commit?.sha || snapshotRes?.commit?.commit?.sha;
      if (edgePids.length) setEdgeFollowups({ status: "waiting_cloud", jobs: [], readError: false });
      startPolling(spaceId, beobachtet, { pids: edgePids, targetStage: edgeStage, sourceSha, dispatchConfirmed });
      return {
        pendingApproval: false,
        dispatchConfirmed,
        runId: beobachtet[0].id,
        runUrl: beobachtet[0].url,
        laeufe,
        pakete: zielPakete,
        failed: nichtGeladen,
        pdFailed: pdNichtGeladen,
      };
    }

    // Kein einziger Lauf auffindbar: der Dispatch ging raus, aber GitHub meldet keine Lauf-Id
    // zurueck. Frueher blieb die Karte dafuer ewig auf "laeuft" stehen und der Startknopf war
    // tot. Eigener Zustand: der Transport ist angestossen, sein Stand steht im CI/CD-Monitor.
    setState("dispatched");
    const keinLaufText = tr("run.noRunFound");
    setError((prev) => (prev ? `${prev} ${keinLaufText}` : keinLaufText));
    return {
      pendingApproval: false,
      keinLauf: true,
      dispatchConfirmed: false,
      laeufe,
      pakete: zielPakete,
      failed: nichtGeladen,
      pdFailed: pdNichtGeladen,
    };
  }, [startPolling]);

  const stop = useCallback(() => {
    operationRef.current?.abort();
    pollingVersion.current += 1;
    if (timerRef.current) timerRef.current();
  }, []);

  return { state, runId, runUrl, runInfo, error, failure, meldungen, edgeFollowups, cloudComplete, snapshotJob, start, stop };
}
